import { useState, useCallback, useEffect } from 'react';
import { fetchClientByCode } from '../services/api';

export const useClientActions = (
  formData,
  setFormData,
  setMessageBoxConfig,
  setShowMessageBox,
  isLoading,
  isNew,
  skipCityAutocompleteRef = null
) => {
  const [clientData, setClientData] = useState(null);
  const [currentClientData, setCurrentClientData] = useState({});
  const [showClientAddressAutocomplete, setShowClientAddressAutocomplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [previousClient, setPreviousClient] = useState(null);

  const fetchClientData = useCallback(async clientCode => {
    if (!clientCode) {
      setClientData(null);
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await fetchClientByCode(clientCode);

      // Vérifier que les données correspondent au client demandé (protection contre race conditions)
      if (data && data.client_code === clientCode) {
        setClientData(data);
        setCurrentClientData(data);

        // Préremplissage automatique des champs de transport pour les nouveaux dossiers
        if (isNew && data.shipping_info_filled) {
          setFormData(prevFormData => {
            const updates = {};
            const booleanFields = [
              'semi_taut',
              'porteur_taut',
              'semi_fourgon',
              'porteur_fourgon',
              'semi_frigo',
              'porteur_frigo',
              'vingt_metres_cube',
              'plateau',
              'tail_lift',
              'pallet_truck',
              'adr',
            ];
            const textFields = ['class_type', 'un_code', 'grp', 'comments'];

            booleanFields.forEach(field => {
              if (data[field] === true) {
                updates[field] = true;
              }
            });

            textFields.forEach(field => {
              if (data[field]) {
                updates[field] = data[field];
              }
            });

            return { ...prevFormData, ...updates };
          });
        }

        return data;
      } else {
        console.warn(
          `Données client incohérentes: demandé ${clientCode}, reçu ${data?.client_code}`
        );
        return null;
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des données client:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const resetClientData = useCallback(() => {
    setClientData(null);
    setError(null);
  }, []);

  // Ces fonctions étaient des hallucinations - supprimées car non utilisées dans l'original

  const handleConfirmClientAddressAutocomplete = useCallback(
    target_address => {
      if (target_address && currentClientData) {
        if (skipCityAutocompleteRef?.current) {
          skipCityAutocompleteRef.current[target_address] = true;
        }
        if (target_address === 'pickup') {
          setFormData(prevFormData => ({
            ...prevFormData,
            pickup_name: currentClientData.pickup_address1
              ? currentClientData.pickup_name
              : currentClientData.name,
            pickup_address1: currentClientData.pickup_address1
              ? currentClientData.pickup_address1
              : currentClientData.establishment_address1,
            pickup_address2: currentClientData.pickup_address1
              ? currentClientData.pickup_address2
              : currentClientData.establishment_address2,
            pickup_postcode: currentClientData.pickup_address1
              ? currentClientData.pickup_postcode
              : currentClientData.establishment_postcode,
            pickup_city: currentClientData.pickup_address1
              ? currentClientData.pickup_city
              : currentClientData.establishment_city,
            pickup_contact: currentClientData.pickup_address1
              ? currentClientData.pickup_contact
              : currentClientData.establishment_contact,
          }));
        } else if (target_address === 'delivery') {
          setFormData(prevFormData => ({
            ...prevFormData,
            delivery_name: currentClientData.pickup_address1
              ? currentClientData.pickup_name
              : currentClientData.name,
            delivery_address1: currentClientData.pickup_address1
              ? currentClientData.pickup_address1
              : currentClientData.establishment_address1,
            delivery_address2: currentClientData.pickup_address1
              ? currentClientData.pickup_address2
              : currentClientData.establishment_address2,
            delivery_postcode: currentClientData.pickup_address1
              ? currentClientData.pickup_postcode
              : currentClientData.establishment_postcode,
            delivery_city: currentClientData.pickup_address1
              ? currentClientData.pickup_city
              : currentClientData.establishment_city,
            delivery_contact: currentClientData.pickup_address1
              ? currentClientData.pickup_contact
              : currentClientData.establishment_contact,
          }));
        }
        setShowClientAddressAutocomplete(false);
      }
    },
    [currentClientData, setFormData, skipCityAutocompleteRef]
  );

  // Surveiller les changements de client (comme dans l'ancien ShipmentForm)
  useEffect(() => {
    const currentClient = formData?.client;

    // Marquer comme initialisé après le premier rendu
    if (!isInitialized) {
      setIsInitialized(true);
      setPreviousClient(currentClient);
      return;
    }

    // Vérifier si c'est un vrai changement de client (pas l'initialisation)
    if (currentClient && currentClient !== previousClient && !isLoading) {
      fetchClientData(currentClient).then(data => {
        if (data) {
          setShowClientAddressAutocomplete(true);
        }
      });
    }

    // Mettre à jour le client précédent
    setPreviousClient(currentClient);
  }, [formData?.client, isLoading, fetchClientData, isInitialized, previousClient]);

  return {
    clientData,
    currentClientData,
    setCurrentClientData,
    showClientAddressAutocomplete,
    setShowClientAddressAutocomplete,
    loading,
    error,
    fetchClientData,
    resetClientData,
    handleConfirmClientAddressAutocomplete,
  };
};
